import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CalendarDays, FileSpreadsheet, FileText, Filter, Calendar, Wallet, DollarSign, CreditCard } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/utils'

export default function AgendaTab({
  agenda,
  filtroDataInicio,
  filtroDataFim,
  setFiltroDataInicio,
  setFiltroDataFim,
  onFiltrar,
  onLimparFiltros,
  onExportarExcel,
  onExportarPDF,
  onPago,
}) {
  const hoje = new Date()
  hoje.setHours(0, 0, 0, 0)

  const total = agenda.reduce((sum, item) => sum + (item.valor || 0), 0)

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-blue-600" />
              Agenda de Pagamentos
            </CardTitle>
            <CardDescription>
              Processos e parcelas aprovadas aguardando pagamento
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onExportarExcel} data-testid="btn-exportar-excel-agenda">
              <FileSpreadsheet className="w-4 h-4 mr-1 text-emerald-600" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={onExportarPDF} data-testid="btn-exportar-pdf-agenda">
              <FileText className="w-4 h-4 mr-1 text-rose-600" />
              PDF
            </Button>
          </div>
        </div>
      </CardHeader>

      <div className="px-6 pb-4">
        <div className="flex flex-wrap items-end gap-4 p-4 bg-slate-50 rounded-lg">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Data Início</Label>
            <Input
              type="date"
              value={filtroDataInicio}
              onChange={(e) => setFiltroDataInicio(e.target.value)}
              className="w-40"
              data-testid="filtro-data-inicio"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Data Fim</Label>
            <Input
              type="date"
              value={filtroDataFim}
              onChange={(e) => setFiltroDataFim(e.target.value)}
              className="w-40"
              data-testid="filtro-data-fim"
            />
          </div>
          <Button onClick={onFiltrar} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-filtrar-agenda">
            <Filter className="w-4 h-4 mr-2" />
            Filtrar
          </Button>
          <Button variant="outline" onClick={onLimparFiltros} data-testid="btn-limpar-filtros">
            Limpar
          </Button>
        </div>
      </div>

      <CardContent className="p-0">
        {agenda.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-500">
            <CalendarDays className="w-12 h-12 mb-4 text-slate-300" />
            <p>Nenhum processo/parcela aprovado na agenda</p>
            <p className="text-sm text-slate-400 mt-1">Aprove processos ou parcelas para adicioná-los à agenda</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="bg-blue-50">
                  <TableHead>Previsão Pgto</TableHead>
                  <TableHead>Número</TableHead>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead className="text-center">Tipo</TableHead>
                  <TableHead>Valor</TableHead>
                  <TableHead>Fonte Recurso</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agenda.map((item, idx) => {
                  const previsaoDate = item.previsao_pagamento ? new Date(item.previsao_pagamento) : null
                  const isAtrasado = previsaoDate && previsaoDate < hoje
                  const isProximo = previsaoDate && !isAtrasado && (previsaoDate.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24) <= 7
                  const semPrevisao = !item.previsao_pagamento
                  const rowKey = item.tipo === 'parcela' ? `${item.processo_id}-p${item.parcela_numero}` : item.processo_id

                  return (
                    <TableRow
                      key={rowKey}
                      className={`hover:bg-slate-50 ${isAtrasado ? 'bg-rose-50' : isProximo ? 'bg-amber-50' : semPrevisao ? 'bg-slate-50' : ''}`}
                      data-testid={`agenda-row-${idx}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className={`w-4 h-4 ${isAtrasado ? 'text-rose-500' : isProximo ? 'text-amber-500' : semPrevisao ? 'text-slate-400' : 'text-blue-500'}`} />
                          {item.previsao_pagamento ? (
                            <>
                              <span className={`font-medium ${isAtrasado ? 'text-rose-700' : isProximo ? 'text-amber-700' : ''}`}>
                                {formatDate(item.previsao_pagamento)}
                              </span>
                              {isAtrasado && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-xs">Atrasado</Badge>}
                              {isProximo && !isAtrasado && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Próximo</Badge>}
                            </>
                          ) : (
                            <span className="text-slate-400 italic">Sem previsão</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{item.numero_processo}</TableCell>
                      <TableCell>{item.fornecedor}</TableCell>
                      <TableCell className="text-center">
                        {item.tipo === 'parcela' ? (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <CreditCard className="w-3 h-3 mr-1" />
                            Parc. {item.parcela_numero}/{item.numero_parcelas}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">
                            Integral
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold text-teal-700">{formatCurrency(item.valor)}</TableCell>
                      <TableCell>
                        {item.fonte_recurso ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Wallet className="w-3 h-3 mr-1" />
                            {item.fonte_recurso}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => onPago(item)}
                                data-testid={`btn-pagar-${idx}`}
                              >
                                <DollarSign className="w-4 h-4 mr-1" />
                                Pagar
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {item.tipo === 'parcela' ? `Pagar parcela ${item.parcela_numero}` : 'Pagar processo'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <div className="px-6 py-4 bg-blue-50 flex justify-between items-center">
              <div className="text-sm text-blue-700">
                <span className="font-semibold">{agenda.length}</span> item(ns) na agenda
              </div>
              <div className="text-sm font-semibold text-blue-700">
                Total: {formatCurrency(total)}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
